import { useTranslation } from "@next-platform/client-common-core";
import {
  CharacterCardVariant,
  Chip,
  ChipSize,
  ChipVariant,
  Icon,
  IconSize,
  IconVariants,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { memo, useMemo } from "react";

import { Row } from "@/components";
import Trans from "@/components/Trans/Trans";
import { getCharacterCardStatusBadgeLabel } from "@/utils";

export type RulebookProfileStatsValues = {
  meters: number;
  hiddenMeters: number;
  rules: number;
  characters: number;
  personas: number;
};

/** Compact profile stats; bots and personas only appear when there are attached items. */
const RulebookProfileStats = ({ statsValues }: { statsValues: RulebookProfileStatsValues }) => {
  const { t } = useTranslation();
  const metersLabel =
    statsValues.hiddenMeters > 0
      ? t("rulebook:profile.stats.metersWithHidden", { count: statsValues.meters, hidden: statsValues.hiddenMeters })
      : t("rulebook:profile.stats.meters", { count: statsValues.meters });
  const rulesLabel = t("rulebook:profile.stats.rules", { count: statsValues.rules });
  const botsLabel = statsValues.characters > 0 ? t("rulebook:profile.stats.bots", { count: statsValues.characters }) : null;
  const personasLabel = statsValues.personas > 0 ? t("rulebook:profile.stats.personas", { count: statsValues.personas }) : null;

  return (
    <Row className="flex-wrap items-center gap-x-4 gap-y-1 text-gray-11" data-testid="RulebookProfile-Stats">
      <span className="inline-flex items-center gap-1.5">
        <Icon icon={IconVariants.CHART_NO_AXES_COMBINED} size={IconSize.XSMALL} />
        <Text variant={TextVariants.LABEL_SMALL_REGULAR}>{metersLabel}</Text>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Icon icon={IconVariants.BOOK_TEXT} size={IconSize.XSMALL} />
        <Text variant={TextVariants.LABEL_SMALL_REGULAR}>{rulesLabel}</Text>
      </span>
      {botsLabel ? (
        <span className="inline-flex items-center gap-1.5">
          <Icon icon={IconVariants.BOT} size={IconSize.XSMALL} />
          <Text variant={TextVariants.LABEL_SMALL_REGULAR}>{botsLabel}</Text>
        </span>
      ) : null}
      {personasLabel ? (
        <span className="inline-flex items-center gap-1.5">
          <Icon icon={IconVariants.USER} size={IconSize.XSMALL} />
          <Text variant={TextVariants.LABEL_SMALL_REGULAR}>{personasLabel}</Text>
        </span>
      ) : null}
    </Row>
  );
};

/** Green "Definition shared" pill — only rendered when the rulebook shares its definition. */
export const DefinitionSharedPill = () => (
  <Chip size={ChipSize.SMALL} variant={ChipVariant.SOFT_SUCCESS} iconStart={IconVariants.EYE} data-testid="RulebookProfile-DefinitionShared">
    <Trans i18nKey="rulebook:profile.definitionShared" />
  </Chip>
);

export const DefinitionPrivatePill = () => (
  <Chip size={ChipSize.SMALL} variant={ChipVariant.SOFT_NEUTRAL} iconStart={IconVariants.LOCK} data-testid="RulebookProfile-DefinitionPrivate">
    <Trans i18nKey="rulebook:profile.definitionPrivate" />
  </Chip>
);

/** Owner status badge — same variant logic the My Rulebooks card shows. */
export const RulebookStatusChip = ({ variant }: { variant: CharacterCardVariant }) => {
  const { t } = useTranslation();
  const label = useMemo(() => getCharacterCardStatusBadgeLabel(variant, t), [variant, t]);
  return label ? <Chip data-testid="RulebookProfile-StatusChip">{label}</Chip> : null;
};

export default memo(RulebookProfileStats);
